import { useRef } from 'react';
import GUIDES from '../lib/guides.generated';

/*
 * The in-app guide. Content comes from docs/guide-*.md via the generated module,
 * so this file only decides how it looks — the words live with the printed
 * handouts and the two can't drift.
 *
 *   guide   'cleaner' | 'town' | 'manager'
 *   onBack  optional; shows a back bar (the worker app is a phone, and needs a
 *           way out that isn't the browser button)
 */

const CSS = `
.tc-guide { color: #cbd5e1; font-size: 15px; line-height: 1.65; }
.tc-guide h2 {
  color: #f1f5f9; font-size: 17px; font-weight: 700;
  margin: 26px 0 10px; padding-top: 18px; border-top: 1px solid #1e293b;
  scroll-margin-top: 12px;
}
.tc-guide h3 { color: #e2e8f0; font-size: 15px; font-weight: 700; margin: 18px 0 6px; }
.tc-guide p { margin: 0 0 12px; }
.tc-guide strong { color: #f8fafc; font-weight: 700; }
.tc-guide em { color: #94a3b8; font-style: italic; }
.tc-guide ul, .tc-guide ol { margin: 0 0 14px; padding-left: 20px; }
.tc-guide li { margin: 0 0 7px; }
.tc-guide blockquote {
  margin: 16px 0; padding: 13px 15px; background: #111827;
  border-left: 3px solid #f97316; border-radius: 8px;
}
.tc-guide blockquote p:last-child, .tc-guide blockquote ol, .tc-guide blockquote ul { margin-bottom: 0; }
.tc-guide table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 14px; }
.tc-guide th {
  background: #1e293b; color: #e2e8f0; text-align: left;
  padding: 8px 10px; border: 1px solid #334155; font-weight: 700;
}
.tc-guide td { padding: 8px 10px; border: 1px solid #334155; vertical-align: top; }
.tc-guide hr { border: 0; border-top: 1px solid #1e293b; margin: 26px 0 0; }
.tc-guide code { background: #1e293b; padding: 1px 5px; border-radius: 4px; font-size: 13px; }
.tc-guide a { color: #60a5fa; }
`;

// Light variant — used only by the manager app's redesigned tabs. The
// worker and Town Portal apps still call this component with the default
// dark theme, so their look stays exactly as it was.
const CSS_LIGHT = `
.tc-guide { color: #1A2B33; font-size: 15px; line-height: 1.65; }
.tc-guide h2 {
  color: #1A2B33; font-size: 17px; font-weight: 700;
  margin: 26px 0 10px; padding-top: 18px; border-top: 1px solid #E4E0D8;
  scroll-margin-top: 12px;
}
.tc-guide h3 { color: #1A2B33; font-size: 15px; font-weight: 700; margin: 18px 0 6px; }
.tc-guide p { margin: 0 0 12px; }
.tc-guide strong { color: #1A2B33; font-weight: 700; }
.tc-guide em { color: #5B6B72; font-style: italic; }
.tc-guide ul, .tc-guide ol { margin: 0 0 14px; padding-left: 20px; }
.tc-guide li { margin: 0 0 7px; }
.tc-guide blockquote {
  margin: 16px 0; padding: 13px 15px; background: #F5F3EF;
  border-left: 3px solid #f97316; border-radius: 8px;
}
.tc-guide blockquote p:last-child, .tc-guide blockquote ol, .tc-guide blockquote ul { margin-bottom: 0; }
.tc-guide table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 14px; }
.tc-guide th {
  background: #F5F3EF; color: #1A2B33; text-align: left;
  padding: 8px 10px; border: 1px solid #E4E0D8; font-weight: 700;
}
.tc-guide td { padding: 8px 10px; border: 1px solid #E4E0D8; vertical-align: top; }
.tc-guide hr { border: 0; border-top: 1px solid #E4E0D8; margin: 26px 0 0; }
.tc-guide code { background: #F5F3EF; color: #1A2B33; padding: 1px 5px; border-radius: 4px; font-size: 13px; }
.tc-guide a { color: #2D5F7C; }
`;

export default function HelpGuide({ guide, onBack, theme = 'dark' }) {
  const bodyRef = useRef(null);
  const entry = GUIDES[guide];
  const light = theme === 'light';

  function jumpTo(id) {
    const el = bodyRef.current?.querySelector(`#${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  if (!entry) {
    return <div style={light ? sLight.missing : s.missing}>No guide available for this account type.</div>;
  }

  const st = light ? sLight : s;

  // Light theme needs a full-bleed background behind the reading column —
  // the reading column itself stays a fixed max-width for line length, so
  // the two live on separate boxes. The dark theme (worker/Town Portal)
  // keeps its original single-box markup untouched.
  const body = (
    <div style={st.wrap}>
      <style>{light ? CSS_LIGHT : CSS}</style>

      {onBack && (
        <button style={st.backBtn} onClick={onBack}>← Back</button>
      )}

      <div style={st.header}>
        <div style={st.title}>{entry.title}</div>
        <div style={st.subtitle}>{entry.subtitle}</div>
      </div>

      <div style={st.supportBanner}>
        Something wrong with the app itself? Email <strong>office@fabiantraverse.com</strong>
      </div>

      <div style={st.contents}>
        <div style={st.contentsLabel}>JUMP TO</div>
        <div style={st.chipRow}>
          {entry.sections.map(sec => (
            <button key={sec.id} style={st.chip} onClick={() => jumpTo(sec.id)}>
              {sec.text}
            </button>
          ))}
        </div>
      </div>

      <div
        ref={bodyRef}
        className="tc-guide"
        dangerouslySetInnerHTML={{ __html: entry.html }}
      />
    </div>
  );

  if (!light) return body;
  return <div style={{ background: '#F5F3EF', minHeight: 'calc(100vh - 130px)', padding: '0 24px' }}>{body}</div>;
}

const s = {
  wrap: { padding: '4px 2px 40px', maxWidth: 760 },
  backBtn: {
    background: 'transparent', border: '1px solid #334155', color: '#94a3b8',
    padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
    marginBottom: 14,
  },
  header: { marginBottom: 16 },
  title: { color: '#f8fafc', fontSize: 21, fontWeight: 800, letterSpacing: -0.3 },
  subtitle: { color: '#94a3b8', fontSize: 13, marginTop: 3 },
  supportBanner: {
    background: '#111827', border: '1px solid #f97316', borderRadius: 10,
    padding: '10px 14px', marginBottom: 16, color: '#e2e8f0', fontSize: 13.5,
  },
  contents: {
    background: '#111827', border: '1px solid #1e293b', borderRadius: 10,
    padding: '12px 14px', marginBottom: 8,
  },
  contentsLabel: { color: '#64748b', fontSize: 10, fontWeight: 700, letterSpacing: 1.2, marginBottom: 8 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: {
    background: '#1e293b', border: '1px solid #334155', color: '#cbd5e1',
    padding: '6px 11px', borderRadius: 999, cursor: 'pointer', fontSize: 12.5,
    textAlign: 'left',
  },
  missing: { color: '#94a3b8', fontSize: 14, padding: 20 },
};

// Light variant styles, manager-only (see CSS_LIGHT above).
const sLight = {
  wrap: { padding: '4px 2px 40px', maxWidth: 760, margin: '0 auto' },
  backBtn: {
    background: '#FFFFFF', border: '1px solid #E4E0D8', color: '#5B6B72',
    padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
    marginBottom: 14,
  },
  header: { marginBottom: 16 },
  title: { color: '#1A2B33', fontSize: 21, fontWeight: 800, letterSpacing: -0.3 },
  subtitle: { color: '#5B6B72', fontSize: 13, marginTop: 3 },
  supportBanner: {
    background: '#FFFFFF', border: '1px solid #f97316', borderRadius: 10,
    padding: '10px 14px', marginBottom: 16, color: '#1A2B33', fontSize: 13.5,
  },
  contents: {
    background: '#FFFFFF', border: '1px solid #E4E0D8', borderRadius: 10,
    padding: '12px 14px', marginBottom: 8,
  },
  contentsLabel: { color: '#5B6B72', fontSize: 10, fontWeight: 700, letterSpacing: 1.2, marginBottom: 8 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: {
    background: '#F5F3EF', border: '1px solid #E4E0D8', color: '#1A2B33',
    padding: '6px 11px', borderRadius: 999, cursor: 'pointer', fontSize: 12.5,
    textAlign: 'left',
  },
  missing: { color: '#5B6B72', fontSize: 14, padding: 20 },
};
